<script setup lang="ts">
import ParameterTable from '../ParameterTable/ParameterTable.vue';
import { Measurement, MeasurementEssentials } from './types';
import { computed } from 'vue';
import { MeasurementStatusDto, ResultDto } from '@/api/generator';
import { useTaskStepTitle } from '../TaskStepTitle/useTaskStepTitles';
import TaskStepTitle from '../TaskStepTitle/TaskStepTitle.vue';
import { truthy } from '@/utils/helper';
import { isEqual } from 'lodash';
import EvaluationState from '@/components/evaluation/EvaluationState.vue';
import Notification from '../notifications/Notification.vue';
import MeasurementStatus from './MeasurementStatus.vue';

const props = defineProps<{
  measurements: Measurement[];
  status?: MeasurementStatusDto;
  currentMeasurement?: Measurement | MeasurementEssentials;
  results?: Record<number, ResultDto | undefined>;
  finished?: boolean;
}>();

function isActive(measurement: Measurement) {
  return isEqual(
    { ...props.currentMeasurement, measurementId: undefined },
    { ...measurement, measurementId: undefined },
  );
}

function getMeasurementParameterCombination(measurement: Measurement) {
  return {
    targetSystemValues: measurement.setup?.targetSystem,
    supportSystemValues: measurement.setup?.supportSystem,
  };
}

const { getTitle } = useTaskStepTitle(
  computed(() => props.measurements.map(getMeasurementParameterCombination).filter(truthy)),
);

function getStepTitle(measurement: Measurement) {
  return getTitle(getMeasurementParameterCombination(measurement));
}

const shouldShowMeasurementStatus = computed(() => (measurement: Measurement) => {
  const measurementStatus = props.status?.measurementStatus;
  return (
    isActive(measurement) &&
    !props.finished &&
    measurementStatus &&
    !['Finished', 'Error'].includes(measurementStatus)
  );
});

const shouldShowFinishedText = computed(() => (index: number) => {
  return !!props.results?.[index]?.output;
});

const shouldShowErrorText = computed(() => (measurement: Measurement) => {
  return isActive(measurement) && props.status?.measurementStatus === 'Error';
});

const shouldShowEvaluationState = computed(() => (measurement: Measurement, index: number) => {
  return props.results?.[index]?.output || measurement.skip;
});

const safeStatus = computed(() => {
  return props.status?.measurementStatus ?? undefined;
});
</script>

<template>
  <v-expansion-panels>
    <v-expansion-panel
      v-for="(measurement, index) in measurements"
      :key="index"
      :disabled="measurement.disabled || measurement.skip"
    >
      <template #title>
        <div class="d-flex align-center w-100">
          <!-- Section 1: Measurement title -->
          <TaskStepTitle
            :combination="getStepTitle(measurement)"
            class="mr-4"
            :class="{
              'font-weight-bold': isActive(measurement) && !finished && status?.measurementStatus,
            }"
          />

          <!-- Section 2: Measurement status -->
          <div
            v-if="
              shouldShowMeasurementStatus(measurement) ||
              shouldShowFinishedText(index) ||
              shouldShowErrorText(measurement)
            "
            class="ms-auto d-flex align-center"
          >
            <MeasurementStatus
              v-if="shouldShowMeasurementStatus(measurement)"
              :status="safeStatus"
              size="compact"
            />

            <!-- Measurement finished -->
            <span v-if="shouldShowFinishedText(index)" class="text-body-2 mr-6 font-weight-medium">
              {{ $t('MeasurementFinished') }}
            </span>

            <!-- Measurement error -->
            <span
              v-if="shouldShowErrorText(measurement)"
              class="pb-2 pt-2 text-body-2 font-weight-medium text-error"
            >
              {{ $t('MeasurementStatus.Error') }}
            </span>
          </div>

          <!-- Section 3: Notifications -->
          <div v-if="measurement.notifications?.length" class="ms-auto d-flex align-center">
            <Notification
              v-for="notification in measurement.notifications"
              :key="notification.message"
              :value="notification"
              density="compact"
            />
          </div>

          <!-- Section 4: Evaluation state -->
          <div v-if="shouldShowEvaluationState(measurement, index)" class="ml-2">
            <EvaluationState
              :value="results?.[index]?.output ?? (measurement.skip ? 'Skipped' : undefined)"
            />
          </div>
        </div>
      </template>

      <template #text>
        <v-row>
          <v-col>
            <ParameterTable
              :title="$t('TargetSystemParameter_other')"
              :parameters="measurement?.setup?.targetSystem"
            />
          </v-col>

          <v-col>
            <ParameterTable
              :title="$t('SupportSystemParameter_other')"
              :parameters="measurement?.setup?.supportSystem"
            />
          </v-col>
        </v-row>
      </template>
    </v-expansion-panel>
  </v-expansion-panels>
</template>
